import { useState } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

interface PhotoUploadFieldProps {
	index: number
	onFileSelect: (file: File, index: number) => void
	onRemove: (index: number) => void
}

export function PhotoUploadField({
	index,
	onFileSelect,
	onRemove,
}: PhotoUploadFieldProps) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const fieldId = `photo-upload-${index}`

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			// Create preview URL
			const url = URL.createObjectURL(file)
			setPreviewUrl(url)
			onFileSelect(file, index)
		}
	}

	const handleRemove = () => {
		setPreviewUrl(null)
		onRemove(index)
		// Reset the input
		const input = document.getElementById(fieldId) as HTMLInputElement
		if (input) {
			input.value = ''
		}
	}

	return (
		<div className="relative">
			<label
				htmlFor={fieldId}
				className={cn(
					'group relative flex size-32 cursor-pointer items-center justify-center rounded-lg transition-colors',
					previewUrl
						? 'bg-transparent'
						: 'border-2 border-dashed border-gray-300 hover:border-gray-400',
				)}
			>
				{previewUrl ? (
					<>
						<img
							src={previewUrl}
							alt={`Upload ${index + 1}`}
							className="size-32 rounded-lg object-cover"
						/>
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault()
								handleRemove()
							}}
							className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
						>
							<Icon name="cross-1" className="size-4" />
						</button>
					</>
				) : (
					<div className="text-center text-gray-400">
						<Icon name="plus" className="mx-auto mb-2 size-8" />
						<span className="text-xs">Foto {index + 1}</span>
					</div>
				)}
			</label>
			<input
				id={fieldId}
				type="file"
				accept="image/*"
				onChange={handleFileChange}
				className="sr-only"
			/>
		</div>
	)
}

interface AIPhotoUploadProps {
	photoCount: number
	sculptureTitle: string
	onSubmit: (files: File[]) => void
	onCancel: () => void
}

export function AIPhotoUpload({
	photoCount,
	onSubmit,
	onCancel,
}: AIPhotoUploadProps) {
	const [selectedFiles, setSelectedFiles] = useState<(File | null)[]>(
		Array(photoCount).fill(null),
	)

	const handleFileSelect = (file: File, index: number) => {
		setSelectedFiles((prev) => {
			const newFiles = [...prev]
			newFiles[index] = file
			return newFiles
		})
	}

	const handleRemove = (index: number) => {
		setSelectedFiles((prev) => {
			const newFiles = [...prev]
			newFiles[index] = null
			return newFiles
		})
	}

	const handleSubmit = () => {
		const files = selectedFiles.filter((f): f is File => f !== null)
		if (files.length > 0) {
			onSubmit(files)
		}
	}

	const selectedCount = selectedFiles.filter((f) => f !== null).length

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-3 gap-3">
				{Array.from({ length: photoCount }).map((_, index) => (
					<PhotoUploadField
						key={index}
						index={index}
						onFileSelect={handleFileSelect}
						onRemove={handleRemove}
					/>
				))}
			</div>

			<div className="flex items-center justify-between pt-4">
				<p className="text-muted-foreground text-sm">
					{selectedCount} van {photoCount} foto{photoCount > 1 ? "'s" : ''}{' '}
					geselecteerd
				</p>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={onCancel}
						className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
					>
						Annuleren
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={selectedCount === 0}
						className={cn(
							'rounded-lg px-4 py-2 text-sm transition-colors',
							selectedCount > 0
								? 'bg-primary text-primary-foreground hover:bg-primary/90'
								: 'cursor-not-allowed bg-gray-100 text-gray-400',
						)}
					>
						Upload {selectedCount} foto{selectedCount !== 1 ? "'s" : ''}
					</button>
				</div>
			</div>
		</div>
	)
}
